@file:OptIn(ExperimentalMaterial3Api::class)

package ui.widget


import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.unit.*
import kotlinx.datetime.*
import org.ssoft.common.tools.*
import provider.*
import ssoft.frontend.generated.resources.*
import tools.*
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant


@Composable
fun DateRangePickerDialog(
    state: DialogState,
    start: EFieldRegularDate,
    end: EFieldRegularDate,
    enabledRange: Pair<LocalDate, LocalDate> = Clock.nowDate() to Clock.nowDate().plus(DatePeriod(days = 364)),
    isValid: (start: LocalDate, end: LocalDate) -> Boolean = { _, _ -> true },
    onChange: () -> Unit = {}
) {

    DateRangePickerDialogInternal(
        state = state,
        start = start.state,
        end = end.state,
        enabledRange = enabledRange,
        isValid = isValid,
        onConfirm = { newStart, newEnd ->
            start.set(newStart)
            end.set(newEnd)
        },
        onChange = onChange
    )
}

@Composable
fun DateRangePickerDialog(
    state: DialogState,
    start: EFieldRegularDateTime,
    end: EFieldRegularDateTime,
    enabledRange: Pair<LocalDate, LocalDate> = Clock.nowDate() to Clock.nowDate().plus(DatePeriod(days = 364)),
    isValid: (start: LocalDate, end: LocalDate) -> Boolean = { _, _ -> true },
    onChange: () -> Unit = {}
) {

    DateRangePickerDialogInternal(
        state = state,
        start = start.state.date,
        end = end.state.date,
        enabledRange = enabledRange,
        isValid = isValid,
        onConfirm = { newStart, newEnd ->
            start.set(newStart + LocalTime.MIN)
            end.set(newEnd + LocalTime.MAX)
        },
        onChange = onChange
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DateWrapper(
    stateDialog: DialogState,
    stateDate: () -> DateRangePickerState,
    dismissButton: @Composable () -> Unit,
    confirmButton: @Composable (DateRangePickerState) -> Unit,
) {

    val isMobile = LocalDisplayProvider.current.isMobile

    Dialog(
        state = stateDialog,
        dismissOnTouchOutside = !isMobile,
        background = if (!isMobile) Color.Transparent
        else MaterialTheme.colorScheme.background
    ) {
        val state = remember {
            stateDate()
        }
//        val state = rememberDateRangePickerState()
        if (isMobile) {
            Column(
                Modifier.fillMaxSize()
                    .padding(top = 16.dp)
            ) {
                DateRangePicker(
                    modifier = Modifier.weight(1F)
                        .fillMaxWidth(),
                    state = state,
                )
                Row(Modifier.fillMaxWidth().padding(vertical = 16.dp, horizontal = 8.dp)) {
                    Spacer(Modifier.weight(1F))
                    dismissButton()
                    confirmButton(state)
                }
            }
        } else {
            DialogUI(
                modifier = Modifier.align(Alignment.Center),
                onDismissRequest = { stateDialog.hide() },
                dismissButton = dismissButton,
                confirmButton = {
                    confirmButton(state)
                }
            ) {
                DateRangePicker(
                    state = state,
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DateRangePickerDialogInternal(
    state: DialogState,
    start: LocalDate,
    end: LocalDate,
    enabledRange: Pair<LocalDate, LocalDate>,
    isValid: (start: LocalDate, end: LocalDate) -> Boolean = { _, _ -> true },
    onConfirm: (start: LocalDate, end: LocalDate) -> Unit,
    onChange: () -> Unit = {}
) {

    val pickerStateFun = {
        val now = Clock.nowDate()

        val yearRange = IntRange(enabledRange.first.year, enabledRange.second.year)

        val rangeMs = LongRange(
            enabledRange.first.toEpochDays().days.inWholeMilliseconds,
            enabledRange.second.toEpochDays().days.inWholeMilliseconds
        )
        DateRangePickerState(
            locale = CalendarLocale.current,
            initialSelectedStartDateMillis = start.toEpochDays().days.inWholeMilliseconds,
            initialSelectedEndDateMillis = end.toEpochDays().days.inWholeMilliseconds,
            initialDisplayedMonthMillis = now.toEpochDays().days.inWholeMilliseconds,
            yearRange = yearRange,
            initialDisplayMode = DisplayMode.Picker,
            selectableDates = object : SelectableDates {
                override fun isSelectableDate(utcTimeMillis: Long): Boolean {
                    return utcTimeMillis in rangeMs
                }

                override fun isSelectableYear(year: Int): Boolean {
                    return year in yearRange
                }
            }
        )
    }

    DateWrapper(
        stateDialog = state,
        stateDate = pickerStateFun,
        dismissButton = {
            TextButton({ state.hide() }) {
                Text(Res.string.dialog__action_cancel.rememberString())
            }
        },
        confirmButton = { pickerState ->
            TextButton({
                pickerState.selectedStartDateMillis
                    ?.let { Instant.fromEpochMilliseconds(it) }
                    ?.toLocalDateTime(TimeZone.currentSystemDefault())
                    ?.date
                    ?.also { newStart ->
                        pickerState.selectedEndDateMillis
                            ?.let { Instant.fromEpochMilliseconds(it) }
                            ?.toLocalDateTime(TimeZone.currentSystemDefault())
                            ?.date
                            ?.also { newEnd ->
                                if (start != newStart || end != newEnd) {
                                    if (isValid(newStart, newEnd)) {
                                        onConfirm(newStart, newEnd)
                                        onChange()
                                        state.hide()
                                    }
                                } else {
                                    state.hide()
                                }
                            } ?: state.hide()
                    } ?: state.hide()
            }) {
                Text(Res.string.dialog__action_ok.rememberString())
            }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DateRangePickerDialog(
    state: DialogState,
    confirm: (start: LocalDate, end: LocalDate) -> Unit
) {
    val pickerStateFun = {
        val now = Clock.nowDate()

        DateRangePickerState(
            locale = CalendarLocale.current,
            initialDisplayedMonthMillis = now.toEpochDays().days.inWholeMilliseconds,
            initialDisplayMode = DisplayMode.Picker
        )
    }

    DateWrapper(
        stateDialog = state,
        stateDate = pickerStateFun,
        dismissButton = {
            TextButton({ state.hide() }) {
                Text(Res.string.dialog__action_cancel.rememberString())
            }
        },
        confirmButton = { pickerState ->
            TextButton({
                pickerState.selectedStartDateMillis
                    ?.let { Instant.fromEpochMilliseconds(it) }
                    ?.toLocalDateTime(TimeZone.currentSystemDefault())
                    ?.date
                    ?.also { newStart ->
                        pickerState.selectedEndDateMillis
                            ?.let { Instant.fromEpochMilliseconds(it) }
                            ?.toLocalDateTime(TimeZone.currentSystemDefault())
                            ?.date
                            ?.also { newEnd ->
                                confirm(newStart, newEnd)
                                state.hide()
                            } ?: state.hide()
                    } ?: state.hide()
            }) {
                Text(Res.string.dialog__action_ok.rememberString())

            }
        }
    )
}
